/**
 * Hero Video State Service
 * Tracks when HERO section video is playing to coordinate media loading
 * Other routes' media should only start loading after HERO video is playing
 */

'use client';

type HeroVideoStateListener = (isPlaying: boolean) => void;

class HeroVideoStateService {
  private isPlaying = false;
  private listeners = new Set<HeroVideoStateListener>();
  private heroVideoUrl: string | null = null;
  private hasStarted = false;

  /**
   * Mark HERO video as playing
   */
  setPlaying(videoUrl: string): void {
    if (this.isPlaying) return; // Already playing
    
    this.heroVideoUrl = videoUrl;
    this.isPlaying = true;
    this.hasStarted = true;
    
    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(true);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[HeroVideoState] Listener error:', error);
        }
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('[HeroVideoState] HERO video is now playing:', videoUrl);
    }
  }

  /**
   * Mark HERO video as stopped/paused
   */
  setStopped(): void {
    if (!this.isPlaying) return;
    
    this.isPlaying = false;
    
    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(false);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[HeroVideoState] Listener error:', error);
        }
      }
    });
  }

  /**
   * Check if HERO video is currently playing
   */
  getIsPlaying(): boolean {
    return this.isPlaying;
  }

  /**
   * Check if HERO video has started playing at least once
   */
  getHasStarted(): boolean {
    return this.hasStarted;
  }

  /**
   * Get the HERO video URL
   */
  getHeroVideoUrl(): string | null {
    return this.heroVideoUrl;
  }

  /**
   * Subscribe to HERO video state changes
   */
  subscribe(listener: HeroVideoStateListener): () => void {
    this.listeners.add(listener);
    
    // Immediately notify if already playing
    if (this.isPlaying) {
      try {
        listener(true);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[HeroVideoState] Listener error:', error);
        }
      }
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Wait for HERO video to start playing
   * Returns a promise that resolves when HERO video is playing
   */
  waitForPlaying(timeoutMs = 30000): Promise<void> {
    return new Promise((resolve, reject) => {
      // If already playing, resolve immediately
      if (this.isPlaying) {
        resolve();
        return;
      }

      // If already started but not playing, resolve anyway (video may have paused)
      if (this.hasStarted) {
        resolve();
        return;
      }

      // Set up listener
      const unsubscribe = this.subscribe((isPlaying) => {
        if (isPlaying) {
          unsubscribe();
          resolve();
        }
      });

      // Set timeout
      const timeoutId = setTimeout(() => {
        unsubscribe();
        // Resolve anyway after timeout - don't block forever
        if (process.env.NODE_ENV === 'development') {
          console.warn('[HeroVideoState] Timeout waiting for HERO video to play');
        }
        resolve();
      }, timeoutMs);

      // Clean up timeout if promise resolves early
      const originalResolve = resolve;
      resolve = () => {
        clearTimeout(timeoutId);
        unsubscribe();
        originalResolve();
      };
    });
  }

  /**
   * Reset state (e.g., on page navigation)
   */
  reset(): void {
    this.isPlaying = false;
    this.heroVideoUrl = null;
    this.hasStarted = false;
  }
}

// Export singleton instance
export const heroVideoState = new HeroVideoStateService();
